import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Boxes } from 'lucide-react';

/**
 * Phase 2I.3c — change which apps an existing user may see.
 *
 * ## Why "All apps" is a mode and not an empty list
 *
 * `app_scope` has two kinds of value and they are not points on one
 * scale: `null` means every app the tenant holds, a list means those
 * apps only. An empty list is folded into `null` by `normalise_scope`
 * everywhere in this feature, so a checkbox list on its own cannot
 * express the difference — unticking everything would read as a lockout
 * to the person doing it and arrive at the server as "give them
 * everything". That is the widest possible outcome from the gesture that
 * looks like the narrowest.
 *
 * So the mode is explicit: All apps, or Specific apps with at least one
 * ticked. Save is blocked in the third state rather than silently
 * treating it as either.
 *
 * ## Why a scoped admin cannot choose "All apps"
 *
 * Decision 2 of the design note: never wider than your own. A MATCH lead
 * granting "every app" would be granting Dispatcher, which they do not
 * have. The server refuses it with a 400; the radio is disabled here so
 * the refusal is visible before the click rather than after — the same
 * argument 2F.4 made for dormant permission keys.
 *
 * Props
 *   isOpen       boolean
 *   user         the target row — `user_id`, `name`, `email`, `app_scope`
 *   apps         [{ app_id, name }] the caller's own apps, from
 *                /api/tenant/sidebar-config. Already narrowed by grant,
 *                by the caller's scope and by their permission keys, so
 *                the list cannot offer more than the caller holds.
 *   callerScope  the caller's own `app_scope`; null/undefined means
 *                unscoped, which is the only case that may grant All.
 *   onClose      () => void
 *   onSave       (userId, appScopeOrNull) => Promise<void>
 */
export default function EditAppScopeModal({
  isOpen,
  user,
  apps,
  callerScope,
  onClose,
  onSave,
}) {
  const options = Array.isArray(apps) ? apps.filter((a) => a?.app_id) : [];
  const callerIsScoped =
    Array.isArray(callerScope) && callerScope.filter(Boolean).length > 0;

  const [mode, setMode] = useState('all');
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Seed from the target's current scope each time the modal opens. An
  // empty or absent scope is "all apps", the same fold applied everywhere
  // else — a user written before 2I must not open as a lockout.
  const current = Array.isArray(user?.app_scope)
    ? user.app_scope.filter(Boolean)
    : [];
  const currentKey = current.join(',');
  useEffect(() => {
    if (!isOpen) return;
    setMode(currentKey ? 'specific' : 'all');
    setSelected(currentKey ? currentKey.split(',') : []);
    setError('');
    setSaving(false);
  }, [isOpen, currentKey]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, saving]);

  const toggle = useCallback((appId) => {
    setSelected((prev) =>
      prev.includes(appId) ? prev.filter((a) => a !== appId) : [...prev, appId],
    );
  }, []);

  if (!isOpen || !user) return null;

  const nothingPicked = mode === 'specific' && selected.length === 0;

  const handleSave = async () => {
    if (nothingPicked) {
      // Never resolved silently in either direction: sending [] would
      // widen to all apps, and treating it as a lockout is not something
      // this feature supports at all.
      setError('Pick at least one app, or choose All apps.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(user.user_id, mode === 'all' ? null : selected);
    } catch (err) {
      setError(err?.message || 'Could not update app access.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="bg-boss-surface border border-boss-border rounded-xl w-full max-w-md p-5"
        data-testid="edit-app-scope-modal"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-600/10 flex items-center justify-center shrink-0">
            <Boxes className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-boss-text">App access</h2>
            <p className="text-xs text-boss-text-secondary mt-0.5 truncate">
              {user.name || user.email}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-boss-text-muted hover:text-boss-text disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm text-boss-text-secondary cursor-pointer">
            <input
              type="radio"
              checked={mode === 'all'}
              disabled={saving || callerIsScoped}
              onChange={() => setMode('all')}
              data-testid="app-scope-mode-all"
              className="mt-0.5"
            />
            <span>
              All apps
              <span className="block text-xs text-boss-text-muted">
                {callerIsScoped
                  ? 'You can only give access to the apps you have yourself.'
                  : 'Everything this workspace has been granted, now and later.'}
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-boss-text-secondary cursor-pointer">
            <input
              type="radio"
              checked={mode === 'specific'}
              disabled={saving}
              onChange={() => setMode('specific')}
              data-testid="app-scope-mode-specific"
              className="mt-0.5"
            />
            <span>Specific apps</span>
          </label>

          {mode === 'specific' && (
            <div
              className="ml-6 space-y-1.5 rounded-lg border border-boss-border p-2.5"
              data-testid="app-scope-list"
            >
              {options.length === 0 ? (
                <p className="text-xs text-boss-text-muted">
                  No apps available to assign.
                </p>
              ) : (
                options.map((app) => (
                  <label
                    key={app.app_id}
                    className="flex items-center gap-2 text-xs text-boss-text-secondary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(app.app_id)}
                      disabled={saving}
                      onChange={() => toggle(app.app_id)}
                      data-testid={`app-scope-app-${app.app_id}`}
                    />
                    <span>{app.name || app.app_id}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        {error && (
          <p
            className="text-xs text-red-600 dark:text-red-400 mt-3"
            data-testid="app-scope-error"
          >
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="app-scope-save"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
